
"use client"

import { useState, useEffect } from "react"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../../firebase/config"
import { useHistory, Redirect } from "react-router-dom"
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Badge,
  Avatar,
  Fade,
} from "@mui/material"
import { useAuth } from "../../contexts/AuthContext"
import SearchIcon from "@mui/icons-material/Search"
import DescriptionIcon from "@mui/icons-material/Description"
import BusinessIcon from "@mui/icons-material/Business"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import RefreshIcon from "@mui/icons-material/Refresh"
import VisibilityIcon from "@mui/icons-material/Visibility"
import PersonIcon from "@mui/icons-material/Person"
import FilterListIcon from "@mui/icons-material/FilterList"
import { doc, getDoc, query, where } from "firebase/firestore"
import api from "../../services/api"

function SeeUploads() {
  const [submissions, setSubmissions] = useState([])
  const [filteredSubmissions, setFilteredSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [anchorEl, setAnchorEl] = useState(null)
  const { currentUser, loading: authLoading } = useAuth()
  const history = useHistory()

  useEffect(() => {
    if (authLoading) return // Wait until auth is ready
    if (!currentUser) return // Redirect will happen in render

    fetchSubmissions()
  }, [currentUser, authLoading])

  useEffect(() => {
    // Filter submissions based on search term and filter status
    if (submissions.length > 0) {
      let filtered = [...submissions]

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(
          (submission) =>
            (submission?.borrowerDetails?.borrowerConstitution || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (submission?.id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (submission?.borrowerDetails?.name || "").toLowerCase().includes(searchTerm.toLowerCase())||
            (submission?.submissionName || "").toLowerCase().includes(searchTerm.toLowerCase()),
        )
      }

      // Apply status filter
      if (filterStatus !== "all") {
        filtered = filtered.filter((submission) => (submission?.status || "pending") === filterStatus)
      }

      setFilteredSubmissions(filtered)
    } else {
      setFilteredSubmissions([])
    }
  }, [searchTerm, filterStatus, submissions])

  // const fetchSubmissions = async () => {
  //   setLoading(true)
  //   setError(null)
  //   try {
  //     const q = query(collection(db, "submissions"), where("userId", "==", currentUser.uid))
  //     const querySnapshot = await getDocs(q)

  //     const data = await Promise.all(
  //       querySnapshot.docs.map(async (docSnap) => {
  //         const docData = docSnap.data()
  //         let reviewerName = null

  //         if (docData.reviewer) {
  //           try {
  //             const reviewerDoc = await getDoc(doc(db, "users", docData.reviewer))
  //             if (reviewerDoc.exists()) {
  //               reviewerName = reviewerDoc.data().name || null
  //             }
  //           } catch (err) {
  //             console.warn(`Error fetching reviewer info for ${docData.reviewer}:`, err)
  //           }
  //         }

  //         return {
  //           id: docSnap.id,
  //           time: docData.time,
  //           submittedBy: docData.submittedBy,
  //           status: docData.status || "pending",
  //           createdAt: docData.createdAt || { seconds: 0 },
  //           reviewerName: reviewerName || "Unassigned",
  //           submissionname: docData.name,
  //           ...docData,
  //         }
  //       }),
  //     )

  //     data.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds)

  //     setSubmissions(data)
  //     setFilteredSubmissions(data)
  //   } catch (err) {
  //     console.error("Error fetching submissions: ", err)
  //     setError("Failed to fetch submissions. Please try again.")
  //   } finally {
  //     setLoading(false)
  //   }
  // }

 const fetchSubmissions = async () => {
    setLoading(true)
    setError(null)
    try {
      // Call your Spring Boot endpoint
      const response = await api.get(`/vetting/submissions/user/${currentUser.uid}`);
      const data = response.data;
      
      // Sort newest first
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setSubmissions(data)
      setFilteredSubmissions(data)
    } catch (err) {
      console.error("Error fetching submissions: ", err)
      setError("Failed to fetch submissions. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetails = (id) => {
    console.log("Navigating to:", `/seeUploads/${id}`)
    history.push(`/seeUploads/${id}`)
  }

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleRefresh = () => {
    fetchSubmissions()
    handleMenuClose()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success"
      case "rejected":
        return "error"
      case "pending":
        return "warning"
      case "under-review":
        return "info"
      default:
        return "default"
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return "✓"
      case "rejected":
        return "✗"
      case "pending":
        return "⏳"
      case "under-review":
        return "👁️"
      default:
        return "?"
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "N/A"
    const date = new Date(timestamp.seconds * 1000)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (authLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "100vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!currentUser) {
    return <Redirect to="/login" />
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your submissions...
        </Typography>
      </Box>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            My Submissions
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleRefresh}>
                <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
                Refresh
              </MenuItem>
              <MenuItem onClick={() => history.push("/upload")}>
                <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                New Submission
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 4,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <TextField
            placeholder="Search submissions..."
            variant="outlined"
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="under-review">Under Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            <Button size="small" onClick={fetchSubmissions} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        )}

        {filteredSubmissions.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              backgroundColor: "background.default",
              borderRadius: 2,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No submissions found
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400, mb: 3 }}>
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your search or filter criteria"
                : "You haven't submitted any documents yet"}
            </Typography>
            {searchTerm || filterStatus !== "all" ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm("")
                  setFilterStatus("all")
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="contained" onClick={() => history.push("/upload")} startIcon={<DescriptionIcon />}>
                Upload Documents
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredSubmissions.map((submission) => (
              <Grid item xs={12} sm={6} md={4} key={submission.id}>
                <Fade in={true} timeout={300}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: 20,
                        zIndex: 1,
                      }}
                    >
                      <Badge
                        overlap="circular"
                        badgeContent={getStatusIcon(submission.status)}
                        color={getStatusColor(submission.status)}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "1rem",
                            height: "28px",
                            width: "28px",
                            borderRadius: "50%",
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "background.paper",
                            border: "2px solid",
                            borderColor: (theme) =>
  theme.palette[getStatusColor(submission.status)]?.main || theme.palette.grey[400],

                            color: (theme) =>
  theme.palette[getStatusColor(submission.status)]?.main || theme.palette.grey[700],

                          }}
                        >
                          <DescriptionIcon />
                        </Avatar>
                      </Badge>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                      <Typography variant="h6" component="h2" gutterBottom noWrap>
                        {submission?.submissionname || "Unnamed Submission"}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {submission?.borrowerDetails?.borrowerConstitution || "N/A"}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Status
                        </Typography>
                        <Chip
                          label={submission.status || "pending"}
                          color={getStatusColor(submission.status)}
                          size="small"
                          sx={{ fontWeight: "medium" }}
                        />
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Submitted
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(submission.submittedAt)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Reviewer
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                          <Typography variant="body2" fontWeight="medium">
                            {submission.reviewerName || "Unassigned"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    <Divider />
                    <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        onClick={() => handleViewDetails(submission.id)}
                        startIcon={<VisibilityIcon />}
                        fullWidth
                      >
                        View Details
                      </Button>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Container>
  )
}

export default SeeUploads
